import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Button, Grid,
    Chip, Divider, Link, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Alert, TextField,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    PlayArrow as ProcessIcon,
    LocalShipping as ShipIcon,
    CheckCircle as DeliverIcon,
    Cancel as CancelIcon,
    Payment as PayIcon,
    Edit as EditIcon,
    CheckCircleOutline as ConfirmIcon,
    Autorenew as ReturnIcon,
} from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import orderService from '../../../services/orderService';
import { useCurrency } from '../../../hooks/useCurrency';
import DeliveryChip, { deliverySummary } from '../../common/DeliveryChip';

const STATUS_COLORS = {
    DRAFT: 'default',
    CONFIRMED: 'success',
    PENDING: 'warning',
    PROCESSING: 'info',
    SHIPPED: 'primary',
    DELIVERED: 'success',
    CANCELLED: 'error',
    RETURN_REQUESTED: 'warning',
    EXCHANGE_REQUESTED: 'warning',
    RETURNED: 'secondary',
    EXCHANGED: 'secondary',
};

const PAYMENT_STATUS_COLORS = {
    PENDING: 'warning',
    PAID: 'success',
    FAILED: 'error',
    REFUNDED: 'default',
};

const OrderDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusNote, setStatusNote] = useState('');

    useEffect(() => { fetchOrder(); }, [id]);

    const fetchOrder = async () => {
        setLoading(true);
        try {
            setOrder(await orderService.getOrderById(id));
        } catch {
            toast.error(t('orders.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleStatus = async (status) => {
        try {
            await orderService.updateStatus(id, status, statusNote || undefined);
            setStatusNote('');
            toast.success(t('orders.statusSuccess'));
            fetchOrder();
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
        }
    };

    const handlePaymentStatus = async (paymentStatus) => {
        try {
            await orderService.updatePaymentStatus(id, paymentStatus);
            toast.success(t('orders.paymentSuccess'));
            fetchOrder();
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
        }
    };

    // Always the system currency (Paramètres › Devise & Système), never a per-order code.
    const { format: formatCurrency } = useCurrency();

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('fr-FR', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const InfoRow = ({ label, value }) => (
        <>
            <Grid item xs={5}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Grid>
            <Grid item xs={7}>
                <Typography variant="body2" fontWeight="medium">{value || '-'}</Typography>
            </Grid>
        </>
    );

    const TotalRow = ({ label, value, color }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={500} color={color}>{value}</Typography>
        </Box>
    );

    if (loading) return <Box sx={{ p: 3 }}><Typography>{t('common.loading')}</Typography></Box>;
    if (!order) return <Box sx={{ p: 3 }}><Alert severity="error">{t('common.notFound')}</Alert></Box>;

    // Admin (staff-created) orders skip the processing/shipping pipeline
    // entirely — DRAFT (editable) -> PENDING (confirm, the sale itself) or
    // CANCELLED, then PENDING is terminal (mirrors DELIVERED being terminal
    // for website orders). Website orders are untouched — see OrderService
    // #validateStatusTransition for the matching backend rule.
    const isAdminOrder = order.source === 'ADMIN';
    const canTransitionStatus = isAdminOrder
        ? order.status === 'DRAFT'
        : ['PENDING', 'PROCESSING', 'SHIPPED'].includes(order.status);
    // Any order except drafts and cancelled ones can be returned/exchanged
    // (the backend enforces the same rule — ReturnRequestService).
    const canCreateReturn = !['DRAFT', 'CANCELLED'].includes(order.status);

    const isOnlineOrder = Boolean(order.shopOrderNumber);
    // Website orders: the delivery fee and the grand total are on the parent online order.
    const deliveryFee = isOnlineOrder ? order.shopShippingAmount ?? 0 : order.shippingAmount ?? 0;
    const grandTotal = isOnlineOrder ? order.shopTotalAmount ?? order.totalAmount : order.totalAmount;
    const hasActions =
        canTransitionStatus || canCreateReturn || (order.paymentStatus === 'PENDING' && order.status !== 'CANCELLED') ||
        (isAdminOrder && order.status === 'DRAFT');

    return (
        <Box sx={{ p: 3 }}>
            {/* Header: who/what/when */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
                    {t('common.back')}
                </Button>
                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h4">{order.orderNumber}</Typography>
                        <Chip label={t(`orders.status.${order.status}`)} color={STATUS_COLORS[order.status]} />
                        <Chip label={t(`orders.paymentStatuses.${order.paymentStatus}`)} color={PAYMENT_STATUS_COLORS[order.paymentStatus]} variant="outlined" />
                        {isOnlineOrder && <Chip label={t('orders.onlineOrder')} color="secondary" variant="outlined" size="small" />}
                        <DeliveryChip order={order} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('orders.orderedAt')} {formatDate(order.orderedAt)}
                    </Typography>
                </Box>
            </Box>

            {/* Actions: the optional note applies to the next status change, so it sits right next to the buttons. */}
            {hasActions && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', py: 2, '&:last-child': { pb: 2 } }}>
                        {canTransitionStatus && (
                            <TextField
                                size="small"
                                label={t('orders.statusNote')}
                                placeholder={t('orders.statusNotePlaceholder')}
                                value={statusNote}
                                onChange={(e) => setStatusNote(e.target.value)}
                                sx={{ flex: '1 1 260px', maxWidth: 420 }}
                            />
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: { md: 'auto' } }}>
                            {isAdminOrder && order.status === 'DRAFT' && (
                                <>
                                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/orders/${order.id}/edit`)}>
                                        {t('orders.editDraft')}
                                    </Button>
                                    <Button variant="contained" color="success" startIcon={<ConfirmIcon />} onClick={() => handleStatus('CONFIRMED')}>
                                        {t('orders.confirmDraft')}
                                    </Button>
                                </>
                            )}
                            {!isAdminOrder && order.status === 'PENDING' && (
                                <Button variant="outlined" startIcon={<ProcessIcon />} onClick={() => handleStatus('PROCESSING')}>
                                    {t('orders.process')}
                                </Button>
                            )}
                            {!isAdminOrder && order.status === 'PROCESSING' && (
                                <Button variant="outlined" startIcon={<ShipIcon />} onClick={() => handleStatus('SHIPPED')}>
                                    {t('orders.ship')}
                                </Button>
                            )}
                            {!isAdminOrder && order.status === 'SHIPPED' && (
                                <Button variant="contained" color="success" startIcon={<DeliverIcon />} onClick={() => handleStatus('DELIVERED')}>
                                    {t('orders.deliver')}
                                </Button>
                            )}
                            {order.paymentStatus === 'PENDING' && order.status !== 'CANCELLED' && (
                                <Button variant="contained" startIcon={<PayIcon />} onClick={() => handlePaymentStatus('PAID')}>
                                    {t('orders.markPaid')}
                                </Button>
                            )}
                            {canCreateReturn && (
                                <Button variant="outlined" startIcon={<ReturnIcon />} onClick={() => navigate(`/returns/create/${order.id}`)}>
                                    {t('nav_ext.createReturn')}
                                </Button>
                            )}
                            {canTransitionStatus && (
                                <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => handleStatus('CANCELLED')}>
                                    {t('orders.status.CANCELLED')}
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Grid container spacing={3}>
                {/* Main content */}
                <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                {/* Client */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('orders.customerSection')}</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={1}>
                                <InfoRow label={t('common.name')} value={order.customerName || t('orders.walkIn')} />
                                <InfoRow
                                    label={t('orders.customerPhone')}
                                    value={order.customerPhone && <Link href={`tel:${order.customerPhone}`} underline="hover">{order.customerPhone}</Link>}
                                />
                                <InfoRow
                                    label={t('orders.customerEmail')}
                                    value={order.customerEmail && <Link href={`mailto:${order.customerEmail}`} underline="hover" sx={{ wordBreak: 'break-all' }}>{order.customerEmail}</Link>}
                                />
                                {order.customerId && (
                                    <InfoRow
                                        label={t('orders.customerAccount')}
                                        value={<Link component="button" type="button" underline="hover" onClick={() => navigate(`/customers/${order.customerId}`)}>{t('orders.viewCustomer')}</Link>}
                                    />
                                )}
                            </Grid>
                            {/* What the customer wrote at checkout ("Instructions pour la livraison"). */}
                            {order.notes && (
                                <Alert severity="info" icon={false} sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {t('orders.customerNote')}
                                    </Typography>
                                    {order.notes}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order information (delivery + address included for website orders) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('orders.orderInformation')}</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={1}>
                                {isOnlineOrder && <InfoRow label={t('orders.shopOrderNumber')} value={order.shopOrderNumber} />}
                                <InfoRow label={t('orders.store')} value={order.storeName} />
                                {order.deliveryMethod && (
                                    <InfoRow label={t('delivery.deliveryColumn')} value={deliverySummary(t, order)} />
                                )}
                                {isOnlineOrder && (
                                    <InfoRow label={t('orders.deliveryFee')} value={formatCurrency(deliveryFee)} />
                                )}
                                {order.shippingAddress && (
                                    <InfoRow
                                        label={t('orders.shippingAddress')}
                                        value={<span style={{ whiteSpace: 'pre-wrap' }}>{order.shippingAddress}</span>}
                                    />
                                )}
                                {order.billingAddress && (
                                    <InfoRow
                                        label={t('orders.billingAddress')}
                                        value={<span style={{ whiteSpace: 'pre-wrap' }}>{order.billingAddress}</span>}
                                    />
                                )}
                                <InfoRow label={t('orders.paymentMethod')} value={order.paymentMethod} />
                                {order.shippedAt && <InfoRow label={t('orders.shippedAt')} value={formatDate(order.shippedAt)} />}
                                {order.deliveredAt && <InfoRow label={t('orders.deliveredAt')} value={formatDate(order.deliveredAt)} />}
                                {/* Only set for ADMIN-source orders — null for website/guest orders (no User to attribute them to). */}
                                {order.createdByName && (
                                    <InfoRow
                                        label={t('orders.createdBy')}
                                        value={`${order.createdByName}${order.createdByEmail ? ` (${order.createdByEmail})` : ''}`}
                                    />
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Line items + totals */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('orders.lineItems')}</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('orders.productCol')}</TableCell>
                                            <TableCell>{t('orders.variantCol')}</TableCell>
                                            <TableCell align="right">{t('common.quantity')}</TableCell>
                                            <TableCell align="right">{t('orders.unitPrice')}</TableCell>
                                            <TableCell>{t('orders.promotionCol')}</TableCell>
                                            <TableCell align="right">{t('orders.discount')}</TableCell>
                                            <TableCell align="right">{t('orders.lineTotal')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.items?.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell>{item.variantName}</TableCell>
                                                <TableCell align="right">{item.quantity}</TableCell>
                                                <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                <TableCell>
                                                    {item.promotionName ? (
                                                        <Chip label={item.promotionName} size="small" color="secondary" />
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                                    {item.discountAmount > 0 ? `-${formatCurrency(item.discountAmount)}` : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                    {formatCurrency(item.totalPrice)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Totals, right-aligned under the table */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Box sx={{ width: { xs: '100%', sm: 340 } }}>
                                    <TotalRow label={t('orders.subtotal')} value={formatCurrency(order.subtotal)} />
                                    {order.discountAmount > 0 && (
                                        <TotalRow label={t('orders.promoDiscount')} value={`-${formatCurrency(order.discountAmount)}`} color="success.main" />
                                    )}
                                    {order.manualDiscountAmount > 0 && (
                                        <TotalRow label={t('orders.manualDiscount')} value={`-${formatCurrency(order.manualDiscountAmount)}`} color="success.main" />
                                    )}
                                    {order.taxAmount > 0 && <TotalRow label={t('orders.taxAmount')} value={formatCurrency(order.taxAmount)} />}
                                    <TotalRow label={t('orders.deliveryFee')} value={formatCurrency(deliveryFee)} />
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Typography variant="h6">{isOnlineOrder ? t('orders.totalToPay') : t('common.total')}</Typography>
                                        <Typography variant="h5" color="primary" fontWeight={700}>{formatCurrency(grandTotal)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                </Grid>
                </Grid>

                {/* Right: Status change history — sticky alongside the main content instead of a full-width block at the bottom */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('orders.statusHistory')}
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />

                            {(!order.statusHistory || order.statusHistory.length === 0) ? (
                                <Typography color="text.secondary">
                                    {t('orders.noStatusHistory')}
                                </Typography>
                            ) : (
                                <Timeline sx={{ p: 0, m: 0 }}>
                                    {order.statusHistory.map((history, i) => (
                                        <TimelineItem key={history.id}>
                                            <TimelineSeparator>
                                                <TimelineDot color={i === 0 ? 'primary' : 'grey'} />
                                                {i < order.statusHistory.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                <Box sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Chip
                                                            label={history.previousStatusLabel || t('orders.statusCreated')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        <Typography>→</Typography>
                                                        <Chip
                                                            label={history.newStatusLabel}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Box>

                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {t('orders.changedBy')} <strong>{history.changedByName}</strong>
                                                        {' '}({history.changedByEmail})
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(history.changedAt)}
                                                    </Typography>

                                                    {history.note && (
                                                        <Typography variant="body2" sx={{
                                                            mt: 0.5, p: 1,
                                                            bgcolor: 'grey.100',
                                                            borderRadius: 1,
                                                            fontStyle: 'italic'
                                                        }}>
                                                            "{history.note}"
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Timeline>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default OrderDetails;
